import AsyncStorage from '@react-native-async-storage/async-storage';

const GAMIFICATION_KEY = '@gamification_data';

// Configuração de níveis
const LEVELS = [
    { level: 1, xpRequired: 0, title: 'Iniciante', icon: '🌱' },
    { level: 2, xpRequired: 100, title: 'Estudante', icon: '📚' },
    { level: 3, xpRequired: 300, title: 'Dedicado', icon: '✨' },
    { level: 4, xpRequired: 600, title: 'Focado', icon: '🎯' },
    { level: 5, xpRequired: 1000, title: 'Avançado', icon: '🚀' },
    { level: 6, xpRequired: 1500, title: 'Expert', icon: '💎' },
    { level: 7, xpRequired: 2100, title: 'Mestre', icon: '👑' },
    { level: 8, xpRequired: 2800, title: 'Elite', icon: '🏆' },
    { level: 9, xpRequired: 3600, title: 'Lenda', icon: '⭐' },
    { level: 10, xpRequired: 4500, title: 'Campeão', icon: '🔥' },
];

// Pontuação de XP
const XP_REWARDS = {
    answerQuestion: 10,      // Responder uma questão
    correctAnswer: 15,       // Acertar uma questão
    completeChallenge: 50,   // Completar desafio diário
    streakBonus: 20,         // Bônus por manter streak (x dias)
    createQuestion: 25,      // Criar uma questão
};

// Dados iniciais
const getInitialData = () => ({
    streak: {
        current: 0,
        longest: 0,
        lastStudyDate: null,
    },
    xp: {
        total: 0,
        level: 1,
    },
    dailyChallenge: {
        questionsGoal: 10,
        questionsAnswered: 0,
        correctAnswers: 0,
        completed: false,
        lastReset: null,
    },
});

// Obter data de hoje no formato YYYY-MM-DD
const getTodayDate = () => {
    const now = new Date();
    return now.toISOString().split('T')[0];
};

// Obter data de ontem
const getYesterdayDate = () => {
    const now = new Date();
    now.setDate(now.getDate() - 1);
    return now.toISOString().split('T')[0];
};

// Carregar dados de gamificação
export const loadGamificationData = async () => {
    try {
        const data = await AsyncStorage.getItem(GAMIFICATION_KEY);
        if (data) {
            const parsed = JSON.parse(data);
            // Verificar e resetar desafio diário se necessário
            const today = getTodayDate();
            if (parsed.dailyChallenge.lastReset !== today) {
                parsed.dailyChallenge = {
                    ...parsed.dailyChallenge,
                    questionsAnswered: 0,
                    correctAnswers: 0,
                    completed: false,
                    lastReset: today,
                };
                await AsyncStorage.setItem(GAMIFICATION_KEY, JSON.stringify(parsed));
            }
            return parsed;
        }
        // Dados iniciais
        const initial = getInitialData();
        initial.dailyChallenge.lastReset = getTodayDate();
        await AsyncStorage.setItem(GAMIFICATION_KEY, JSON.stringify(initial));
        return initial;
    } catch (error) {

        return getInitialData();
    }
};

// Salvar dados de gamificação
export const saveGamificationData = async (data) => {
    try {
        await AsyncStorage.setItem(GAMIFICATION_KEY, JSON.stringify(data));
        return true;
    } catch (error) {

        return false;
    }
};

// Calcular nível baseado no XP total
export const calculateLevel = (totalXp) => {
    let currentLevel = LEVELS[0];
    for (const level of LEVELS) {
        if (totalXp >= level.xpRequired) {
            currentLevel = level;
        } else {
            break;
        }
    }
    return currentLevel;
};

// Calcular XP para o próximo nível
export const getXpToNextLevel = (totalXp) => {
    const currentLevel = calculateLevel(totalXp);
    const nextLevelIndex = LEVELS.findIndex(l => l.level === currentLevel.level) + 1;
    if (nextLevelIndex < LEVELS.length) {
        return LEVELS[nextLevelIndex].xpRequired - totalXp;
    }
    return 0; // Nível máximo
};

// Calcular progresso para o próximo nível (0-100)
export const getLevelProgress = (totalXp) => {
    const currentLevel = calculateLevel(totalXp);
    const currentLevelXp = currentLevel.xpRequired;
    const nextLevelIndex = LEVELS.findIndex(l => l.level === currentLevel.level) + 1;

    if (nextLevelIndex >= LEVELS.length) {
        return 100; // Nível máximo
    }

    const nextLevelXp = LEVELS[nextLevelIndex].xpRequired;
    const xpInCurrentLevel = totalXp - currentLevelXp;
    const xpNeededForLevel = nextLevelXp - currentLevelXp;

    return Math.round((xpInCurrentLevel / xpNeededForLevel) * 100);
};

// Atualizar streak
export const updateStreak = async () => {
    const data = await loadGamificationData();
    const today = getTodayDate();
    const yesterday = getYesterdayDate();

    // Já estudou hoje
    if (data.streak.lastStudyDate === today) {
        return data.streak;
    }

    // Estudou ontem - continua streak
    if (data.streak.lastStudyDate === yesterday) {
        data.streak.current += 1;
    }
    // Não estudou ontem - reseta streak
    else if (data.streak.lastStudyDate !== null) {
        data.streak.current = 1;
    }
    // Primeira vez estudando
    else {
        data.streak.current = 1;
    }

    // Atualiza maior streak
    if (data.streak.current > data.streak.longest) {
        data.streak.longest = data.streak.current;
    }

    data.streak.lastStudyDate = today;
    await saveGamificationData(data);

    return data.streak;
};

// Adicionar XP
export const addXP = async (amount, reason = 'generic') => {
    const data = await loadGamificationData();
    const oldLevel = calculateLevel(data.xp.total);

    data.xp.total += amount;
    const newLevel = calculateLevel(data.xp.total);

    await saveGamificationData(data);

    return {
        xpGained: amount,
        totalXp: data.xp.total,
        leveledUp: newLevel.level > oldLevel.level,
        oldLevel,
        newLevel,
        reason,
    };
};

// Registrar resposta de questão
export const recordQuestionAnswer = async (isCorrect) => {
    const data = await loadGamificationData();
    let xpGained = XP_REWARDS.answerQuestion;

    // Atualiza desafio diário
    data.dailyChallenge.questionsAnswered += 1;
    if (isCorrect) {
        data.dailyChallenge.correctAnswers += 1;
        xpGained += XP_REWARDS.correctAnswer;
    }

    // Verifica se completou desafio
    let challengeCompleted = false;
    if (!data.dailyChallenge.completed && data.dailyChallenge.questionsAnswered >= data.dailyChallenge.questionsGoal) {
        data.dailyChallenge.completed = true;
        xpGained += XP_REWARDS.completeChallenge;
        challengeCompleted = true;
    }

    // Adiciona XP
    const oldLevel = calculateLevel(data.xp.total);
    data.xp.total += xpGained;
    const newLevel = calculateLevel(data.xp.total);

    // Atualiza streak
    const today = getTodayDate();
    const yesterday = getYesterdayDate();

    if (data.streak.lastStudyDate !== today) {
        if (data.streak.lastStudyDate === yesterday) {
            data.streak.current += 1;
        } else {
            data.streak.current = 1;
        }

        if (data.streak.current > data.streak.longest) {
            data.streak.longest = data.streak.current;
        }

        // Bônus de streak
        if (data.streak.current > 1) {
            const streakBonus = Math.min(data.streak.current * XP_REWARDS.streakBonus, 200);
            data.xp.total += streakBonus;
            xpGained += streakBonus;
        }

        data.streak.lastStudyDate = today;
    }

    await saveGamificationData(data);

    return {
        xpGained,
        totalXp: data.xp.total,
        leveledUp: newLevel.level > oldLevel.level,
        newLevel,
        streak: data.streak,
        dailyChallenge: data.dailyChallenge,
        challengeCompleted,
    };
};

// Registrar criação de questão
export const recordQuestionCreated = async () => {
    return await addXP(XP_REWARDS.createQuestion, 'create_question');
};

// Obter dados do desafio diário
export const getDailyChallengeData = async () => {
    const data = await loadGamificationData();
    return {
        ...data.dailyChallenge,
        progress: Math.round((data.dailyChallenge.questionsAnswered / data.dailyChallenge.questionsGoal) * 100),
        remaining: Math.max(0, data.dailyChallenge.questionsGoal - data.dailyChallenge.questionsAnswered),
    };
};

// Alterar meta diária
export const setDailyGoal = async (newGoal) => {
    const data = await loadGamificationData();
    data.dailyChallenge.questionsGoal = newGoal;
    // Recalcula se completou
    data.dailyChallenge.completed = data.dailyChallenge.questionsAnswered >= newGoal;
    await saveGamificationData(data);
    return data.dailyChallenge;
};

// Obter estatísticas completas
export const getGamificationStats = async () => {
    const data = await loadGamificationData();
    const level = calculateLevel(data.xp.total);
    const progress = getLevelProgress(data.xp.total);
    const xpToNext = getXpToNextLevel(data.xp.total);

    return {
        streak: data.streak,
        xp: {
            total: data.xp.total,
            level: level.level,
            title: level.title,
            icon: level.icon,
            progress,
            xpToNextLevel: xpToNext,
        },
        dailyChallenge: {
            ...data.dailyChallenge,
            progress: Math.round((data.dailyChallenge.questionsAnswered / data.dailyChallenge.questionsGoal) * 100),
            remaining: Math.max(0, data.dailyChallenge.questionsGoal - data.dailyChallenge.questionsAnswered),
        },
    };
};

// Exportar configurações
export { LEVELS, XP_REWARDS };
